"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.asyncIterableToArray = void 0;
exports.arrayToAsyncIterable = arrayToAsyncIterable;
exports.mapAsyncIterable = mapAsyncIterable;
exports.sortAsyncIterable = sortAsyncIterable;
exports.parallelMapAsyncIterable = parallelMapAsyncIterable;
exports.parallelFlatMapAsyncIterable = parallelFlatMapAsyncIterable;
exports.batchAsyncIterable = batchAsyncIterable;
const assert_1 = __importDefault(require("assert"));
/** Convert the given `array` to an `AsyncIterable`. */
async function* arrayToAsyncIterable(array) {
    for (const item of array) {
        yield item;
    }
}
/**
 * Convert the given `iterable` to an `Array`.
 *
 * TODO: when on Node v20, use `Array.fromAsync` instead.
 */
const asyncIterableToArray = async (iterable) => {
    const items = [];
    for await (const item of iterable) {
        items.push(item);
    }
    return items;
};
exports.asyncIterableToArray = asyncIterableToArray;
/** `Array#map`, but for iterables. */
async function* mapAsyncIterable(iterable, fn) {
    for await (const item of iterable) {
        yield fn(item);
    }
}
/**
 * `Array#sort`, but for iterables.
 *
 * This is _extremely slow_ - it requires loading the entire dataset into memory.
 */
async function sortAsyncIterable(iterable, fn) {
    const array = await (0, exports.asyncIterableToArray)(iterable);
    array.sort(fn);
    return arrayToAsyncIterable(array);
}
/**
 * An asynchronous map operation that keeps up to `options.queueLimit` `fn` operations
 * in flight simultaneously. If the resulting iterable is drained more slowly than `fn`
 * can produce results, further `fn` calls will be delayed until the queue starts
 * draining.
 *
 * `fn` should avoid doing anything too expensive before its first `await` point; the
 * generator won't yield anything until after `options.queueLimit` worth of `fn`s
 * have started working.
 */
async function* parallelMapAsyncIterable(iterable, fn, { queueLimit }) {
    (0, assert_1.default)(queueLimit > 0, 'queueLimit must be greater than 0');
    const queue = [];
    for await (const input of iterable) {
        // Just starting the operation, not awaiting yet ensure `queueLimit`
        // operations are in flight before we start yielding results of any of them.
        queue.push(fn(input));
        if (queue.length >= queueLimit) {
            const nextOutPromise = queue.shift();
            // If the caller is processing results more quickly than we can make new
            // ones, this will be an *unresolved* promise and the await will delay until
            // the next read finishes. If consuming is slower than producing, then the
            // queue will consist mostly of already-resolved promises.
            yield await nextOutPromise;
        }
    }
    while (queue.length > 0) {
        const nextOutPromise = queue.shift();
        yield await nextOutPromise;
    }
}
/**
 * An asynchronous flatMap operation that keeps up to `options.queueLimit` `fn` operations
 * in flight simultaneously. If the resulting iterable is drained more slowly than `fn`
 * can produce results, further `fn` calls will be delayed until the queue starts
 * draining.
 *
 * `fn` should avoid doing anything too expensive before its first `await` point; the
 * generator won't yield anything until after `options.queueLimit` worth of `fn`s
 * have started working.
 */
async function* parallelFlatMapAsyncIterable(iterable, fn, { queueLimit }) {
    (0, assert_1.default)(queueLimit > 0, 'queueLimit must be greater than 0');
    const queue = [];
    for await (const input of iterable) {
        // Just starting the operation, not awaiting yet ensure `queueLimit`
        // operations are in flight before we start yielding results of any of them.
        queue.push(fn(input));
        if (queue.length >= queueLimit) {
            const nextOutPromise = queue.shift();
            // If the caller is processing results more quickly than we can make new
            // ones, this will be an *unresolved* promise and the await will delay until
            // the next read finishes. If consuming is slower than producing, then the
            // queue will consist mostly of already-resolved promises.
            const nextBatch = await nextOutPromise;
            for (const item of nextBatch) {
                yield item;
            }
        }
    }
    while (queue.length > 0) {
        const nextOutPromise = queue.shift();
        const nextBatch = await nextOutPromise;
        for (const item of nextBatch) {
            yield item;
        }
    }
}
/**
 * Buffers up to `batchSize` results at a time from the source iterable
 * and yields batches of results at a time.
 *
 * batchAsyncIterable([1, 2, 3, 4, 5], 2) -> [[1, 2], [3, 4], [5]]
 */
async function* batchAsyncIterable(iterable, batchSize) {
    (0, assert_1.default)(batchSize > 0, 'batchSize must be greater than 0');
    const buffer = [];
    for await (const item of iterable) {
        buffer.push(item);
        if (buffer.length >= batchSize) {
            yield [...buffer];
            buffer.length = 0;
        }
    }
    if (buffer.length) {
        yield [...buffer];
    }
}
//# sourceMappingURL=asyncIterableUtils.js.map